import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber"; // Import Warning Icon
import groupIcon from "../../../assets/icons/group.svg";
import { saveCrifScore } from "../../../services/customer-services/crifService";
import { useCommonContext } from "../../../context/common";

const CrifScoreCard = ({
  score,
  lastUpdated,
  isLoading,
  onRefresh,
  editable = true,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(score);
  const [isSaving, setIsSaving] = useState(false);
  const [outdated, setOutdated] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("0075a4");
  const { clientId } = useCommonContext();

  useEffect(() => {
    setEditValue(score);
    setBackgroundColor(getScoreColor(score));
  }, [score]);

  useEffect(() => {
    setOutdated(isOutdated());
  }, [lastUpdated]);

  // Logic to check if score is outdated (null or > 8 weeks)
  const isOutdated = () => {
    if (!lastUpdated) return true;

    const lastDate = new Date(lastUpdated);
    const today = new Date();
    const eightWeeksInMs = 8 * 7 * 24 * 60 * 60 * 1000;

    return today - lastDate > eightWeeksInMs;
  };

  const handleSave = async () => {
    setIsSaving(true);
    console.log(clientId);
    const response = await saveCrifScore(editValue, clientId);
    if (response.isSuccess) {
      setIsEditing(false);
      if (onRefresh) onRefresh();
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    setEditValue(score);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("dashboard_crif_never");
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getScoreColor = (value) => {
    if (value == null || isNaN(value)) return "#0075a4"; // default fallback

    const numericValue = Number(value);

    if (numericValue < 149) return "#118547";
    if (numericValue <= 199) return "#19B152";
    if (numericValue <= 249) return "#87C449";
    if (numericValue <= 299) return "#FFDE27";
    if (numericValue <= 349) return "#F79124";
    if (numericValue <= 499) return "#F26D22";
    if (numericValue <= 599) return "#EE2326";
    return "#C12525";
  };

  return (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${backgroundColor}, ${backgroundColor}dd)`,
        color: "white",
        padding: "10px",
        borderRadius: "10px",
        height: "100%",
        position: "relative",
        // Optional: Subtle red glow if outdated
        boxShadow: outdated ? "0 0 10px rgba(255, 152, 0, 0.4)" : "none",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              background: "#fff",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img src={groupIcon} alt="Crif" />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Warning Icon for Outdated Score */}
            {outdated && !isLoading && (
              <Tooltip title={t("crif_outdated_tooltip")}>
                <WarningAmberIcon sx={{ color: "#000000" }} />
              </Tooltip>
            )}
            {editable &&
              (!isEditing ? (
                <IconButton
                  size="small"
                  onClick={() => setIsEditing(true)}
                  sx={{ color: "white" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : (
                <Box>
                  <IconButton
                    size="small"
                    onClick={handleSave}
                    disabled={isSaving}
                    sx={{ color: "white" }}
                  >
                    {isSaving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CheckIcon fontSize="small" />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancel}
                    sx={{ color: "white" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Box>

        <Box sx={{ paddingTop: "1rem" }}>
          <Typography sx={{ fontWeight: 600 }}>
            {t("dashboard_crif_burgel_score")}
          </Typography>

          {isEditing && editable ? (
            <TextField
              variant="standard"
              type="number"
              value={editValue ?? ""}
              onChange={(e) => setEditValue(e.target.value)}
              autoFocus
              sx={{
                input: { color: "white", fontWeight: 600, fontSize: "1.5rem" },
                "& .MuiInput-underline:before": { borderBottomColor: "white" },
                "& .MuiInput-underline:after": { borderBottomColor: "white" },
              }}
            />
          ) : (
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, paddingTop: "4px" }}
            >
              {isLoading ? "..." : score ?? "N/A"}
            </Typography>
          )}

          <Box sx={{ mt: 1, display: "flex", flexDirection: "column" }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {t("dashboard_crif_last_updated")}: {formatDate(lastUpdated)}
            </Typography>

            {/* Outdated text message */}
            {outdated && (
              <Typography
                variant="caption"
                sx={{ color: "#ffeb3b", fontWeight: "bold", mt: 0.5 }}
              >
                {t("dashboard_crif_please_update_score")}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CrifScoreCard;
